import { TRAVELINFO_TRIP_BASEURL, TRAVELINFO_TRIP_KEY } from '../constants'
import { isTripValid, mapTripsToLijnen } from './mappers'
import { trackException } from '../irtis-3/utils/application-insights.utils'

import { enrichTripsLineDirections } from './enrichment.service'

async function getMultivertrekkenForStop({
  haltenummer,
  type = 'MERGEFLEX',
  exploitationDate,
}) {
  const searchParams = new URLSearchParams({
    type,
    enrichmentType: 'NONE',
  })

  if (exploitationDate) {
    searchParams.set('exploitationDate', exploitationDate)
  }

  const url =
    `${TRAVELINFO_TRIP_BASEURL}/stops/${haltenummer}/trips?` +
    searchParams.toString()
  const { trips, serverDateTime, statusCode, message } = await fetch(url, {
    headers: {
      'Ocp-Apim-Subscription-Key': TRAVELINFO_TRIP_KEY,
    },
  }).then((data) => data.json())

  const enrichedTrips = await enrichTripsLineDirections(trips)

  if (statusCode && statusCode !== 200) {
    const error = new Error('MultivertrekkenForStop error: ' + message)
    trackException({
      error,
      properties: {
        url,
        statusCode,
      },
    })
    throw error
  }
  const lijnen = mapTripsToLijnen(enrichedTrips)

  return {
    lijnen,
    serverTijd: serverDateTime,
  }
}

export async function getMultivertrekken({
  haltenummers,
  aantal,
  includeEindHaltes,
}) {
  const haltenummerArray = haltenummers.split('+')
  const results = await Promise.allSettled(
    haltenummerArray.map((haltenummer) =>
      getMultivertrekkenForStop({
        haltenummer,
        type: 'MERGEFLEX',
      }),
    ),
  )

  let hasErrors = false
  let serverTijd = Date.now()
  const lijnen = results
    .reduce((allLines, nextResult) => {
      if (nextResult.status === 'fulfilled') {
        serverTijd = nextResult.value.serverTijd || serverTijd
        return allLines.concat(nextResult.value.lijnen)
      }

      hasErrors = true
      return allLines
    }, [])
    .filter((trip) => isTripValid(trip, includeEindHaltes, serverTijd))
    .sort((a, b) => {
      return getPassageTime(a) - getPassageTime(b)
    })
    .slice(0, aantal)

  if (!lijnen.length && hasErrors) {
    throw new Error('No lines were returned and at least one call had an error')
  }

  return {
    lijnen,
    serverTijd,
  }
}

export async function getTheoreticalVertrekken({
  haltenummers,
  exploitationDate,
}) {
  const results = await Promise.allSettled(
    haltenummers.map((haltenummer) =>
      getMultivertrekkenForStop({
        haltenummer,
        type: 'PLANNING',
        exploitationDate,
      }),
    ),
  )

  let hasErrors = false

  const lijnen = results
    .reduce((allLines, nextResult) => {
      if (nextResult.status === 'fulfilled') {
        allLines.push(mapToHalteDoorkomsten(nextResult.value.lijnen))
      } else {
        hasErrors = true
      }

      return allLines
    }, [])
    .sort((a, b) => {
      return a.vertrekTheoretischeTijdstip - b.vertrekTheoretischeTijdstip
    })

  if (!lijnen.length && hasErrors) {
    throw new Error('No lines were returned and at least one call had an error')
  }

  return lijnen
}

export function getPassageTime(passage) {
  return passage.eindHalte
    ? passage.aankomstRealtimeTijdstip || passage.aankomstTheoretischTijdstip
    : passage.vertrekRealtimeTijdstip || passage.vertrekTheoretischeTijdstip
}

export function mapToHalteDoorkomsten(lijnen) {
  const mappedLijnen = lijnen.map((lijnToMap) => ({
    bestemming: lijnToMap.bestemming,
    tijdstipAsLong:
      lijnToMap.vertrekTheoretischeTijdstip ||
      lijnToMap.aankomstTheoretischTijdstip,
    lijn: {
      bestemming: lijnToMap.bestemming,
      lijnNummerPubliek: lijnToMap.lijnNummerPubliek,
      lijnrichting: lijnToMap.lijnRichting,
      omschrijving: lijnToMap.lijnRichting,
      predictionStatussen: lijnToMap.predictionStatussen,
      richtingCode: lijnToMap.richtingCode,
      vertrekRealtimeTijdstip: lijnToMap.vertrekRealtimeTijdstip,
      vertrekTheoretischeTijdstip: lijnToMap.vertrekTheoretischeTijdstip,
      aankomstRealtimeTijdstip: lijnToMap.aankomstRealtimeTijdstip,
      aankomstTheoretischTijdstip: lijnToMap.aankomstTheoretischTijdstip,
      kleurAchterGrond: lijnToMap.kleurAchterGrond,
      kleurAchterGrondRand: lijnToMap.kleurAchterGrondRand,
      kleurVoorGrond: lijnToMap.kleurVoorGrond,
      kleurVoorGrondRand: lijnToMap.kleurVoorGrondRand,
      eindHalte: lijnToMap.eindHalte,
    },
  }))
  return {
    halteDoorkomsten: mappedLijnen,
  }
}
